// src/components/profile/ManagerProfile.jsx
import React, { useState, useEffect } from 'react';
import { Trophy, Star, TrendingUp, Zap, Activity, Award, Shield, Target, Dribbble, DollarSign,  Package,} from 'lucide-react';
import { useLeagueData, useManagerContext } from '../../context/DataContext';
import RiskProfileSection from './risk-profile-section';
import PlayerBadges from './badges/PlayerBadges';
import ArchetypeReveal from '../my-journey/ArchetypeReveal';

// Component for displaying archetype details when clicked
const ArchetypeDetails = ({ archetype, onClose }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  
  // Effect for animation
  useEffect(() => {
    // Slight delay to allow the backdrop to appear first
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 50);
    
    return () => clearTimeout(timer);
  }, []);
  
  // Archetype descriptions and graphics
  const archetypeInfo = {
    'Captain Fantastic': {
      description: 'You have a knack for choosing the right captain at the right time. Your captain selections consistently deliver big hauls, showcasing your excellent player judgment.',
      color: 'from-amber-600 to-amber-900',
      icon: <Trophy size={48} className="text-amber-200" />,
      image: '/images/character-classes/captain-fantastic.jpg'
    },
    'Warren Buffett': {
      description: 'You\'re the financial wizard of FPL! Your team value consistently outperforms others, showing your market savvy and investment acumen.',
      color: 'from-emerald-600 to-emerald-900',
      icon: <Star size={48} className="text-emerald-200" />,
      image: '/images/character-classes/warren-buffett.jpg'
    },
    'King of The Hill': {
      description: 'The undisputed ruler of your FPL league. You\'ve dominated from the top position, showing mastery of all FPL elements.',
      color: 'from-purple-600 to-purple-900',
      icon: <Trophy size={48} className="text-purple-200" />,
      image: '/images/character-classes/king-of-the-hill.jpg'
    },
    'The Bridesmaid': {
      description: 'So close, yet so far! You\'ve been one of the top contenders all season, always in sight of the crown but not quite wearing it.',
      color: 'from-blue-600 to-blue-900',
      icon: <Award size={48} className="text-blue-200" />,
      image: '/images/character-classes/the-bridesmaid.jpg'
    },
    'The Slump King': {
      description: 'Nobody endures a downturn quite like you. You\'ve weathered significant slumps during the season, showing remarkable resilience.',
      color: 'from-red-600 to-red-900',
      icon: <TrendingUp size={48} className="rotate-180 text-red-200" />,
      image: '/images/character-classes/the-slump-king.jpg'
    },
    'The Glory Hunter': {
      description: 'When you\'re hot, you\'re scorching! You\'ve enjoyed exceptional stretches of above-average performance in the league.',
      color: 'from-yellow-600 to-yellow-900',
      icon: <Zap size={48} className="text-yellow-200" />,
      image: '/images/character-classes/the-glory-hunter.jpg'
    },
    'The Grinder': {
      description: 'When life knocks you down, you get back up stronger. You\'ve shown remarkable recovery after difficult periods, displaying true grit.',
      color: 'from-stone-600 to-stone-900',
      icon: <Activity size={48} className="text-stone-200" />,
      image: '/images/character-classes/the-grinder.jpg'
    },
    'The Closer': {
      description: 'When it matters most, you deliver. Your end-of-season performance stands out, showing your clutch decision-making when the pressure is on.',
      color: 'from-indigo-600 to-indigo-900',
      icon: <TrendingUp size={48} className="text-indigo-200" />,
      image: '/images/character-classes/the-closer.jpg'
    },
    'The Fortune Teller': {
      description: 'Luck? What luck? Your uncanny ability to make the right moves at the right time suggests you can see the future.',
      color: 'from-cyan-600 to-cyan-900',
      icon: <Star size={48} className="text-cyan-200" />,
      image: '/images/character-classes/the-fortune-teller.jpg'
    },
    'The Unlucky General': {
      description: 'The fantasy gods have not smiled upon you. Despite your best plans, fate has conspired against you with unfortunate timing and bad luck.',
      color: 'from-slate-600 to-slate-900',
      icon: <Zap size={48} className="text-slate-200" />,
      image: '/images/character-classes/the-unlucky-general.jpg'
    },
    'The Mastermind': {
      description: 'Your strategic brilliance has been on full display. You consistently make moves that are one step ahead of the competition.',
      color: 'from-violet-600 to-violet-900',
      icon: <Trophy size={48} className="text-violet-200" />,
      image: '/images/character-classes/the-mastermind.jpg'
    },
    'The Risk Taker': {
      description: 'Fortune favors the bold! Your willingness to make differential picks and unconventional choices has set you apart from the crowd.',
      color: 'from-rose-600 to-rose-900',
      icon: <Zap size={48} className="text-rose-200" />,
      image: '/images/character-classes/the-risk-taker.jpg'
    },
    'The Steadfast': {
      description: 'Consistency is your hallmark. While others ride the rollercoaster of FPL emotions, you maintain a steady course.',
      color: 'from-teal-600 to-teal-900',
      icon: <Activity size={48} className="text-teal-200" />,
      image: '/images/character-classes/the-steadfast.jpg'
    },
    'The Armband Whisperer': {
      description: 'You have an innate sense for captain selection, consistently making good choices that minimize missed opportunities.',
      color: 'from-amber-600 to-amber-800',
      icon: <Trophy size={48} className="text-amber-200" />,
      image: '/images/character-classes/the-armband-whisperer.jpg'
    },
    'The Treasury Keeper': {
      description: 'A master of the FPL market, you build and maintain team value with skill, creating a strong financial foundation for your squad.',
      color: 'from-emerald-600 to-emerald-800',
      icon: <DollarSign size={48} className="text-emerald-200" />,
      image: '/images/character-classes/the-treasury-keeper.jpg'
    },
    'The Silver Knight': {
      description: 'A noble competitor who consistently finishes in the upper ranks of the league, respected for your reliable performance.',
      color: 'from-gray-500 to-gray-800',
      icon: <Award size={48} className="text-gray-200" />,
      image: '/images/character-classes/the-silver-knight.jpg'
    },
    'The Dark Horse': {
      description: 'Unpredictable and capable of brilliance when least expected. While you may not rank at the very top overall, on your day, you\'re unstoppable.',
      color: 'from-slate-700 to-slate-900',
      icon: <Zap size={48} className="text-slate-300" />,
      image: '/images/character-classes/the-dark-horse.jpg'
    },
    'The Battle-Scarred': {
      description: 'You\'ve endured significant trials during the FPL season, weathering difficult periods below average but emerging stronger for the experience.',
      color: 'from-red-700 to-red-900',
      icon: <Shield size={48} className="text-red-300" />,
      image: '/images/character-classes/the-battle-scarred.jpg'
    },
    'The Phoenix': {
      description: 'Rising from the ashes of defeat, you\'ve transformed your season after a slump, demonstrating remarkable recovery powers.',
      color: 'from-orange-600 to-orange-900',
      icon: <TrendingUp size={48} className="text-orange-300" />,
      image: '/images/character-classes/the-phoenix.jpg'
    },
    'The Endgame Tactician': {
      description: 'When the season enters its final phase, you find another gear. Your strategic prowess in the endgame is formidable.',
      color: 'from-blue-700 to-blue-900',
      icon: <Target size={48} className="text-blue-300" />,
      image: '/images/character-classes/the-endgame-tactician.jpg'
    },
    'The Star-Crossed': {
      description: 'Fate seems to conspire against your best-laid plans, with luck rarely falling in your favor despite sound decision-making.',
      color: 'from-purple-700 to-purple-900',
      icon: <Star size={48} className="text-purple-300" />,
      image: '/images/character-classes/the-star-crossed.jpg'
    },
    'The Favored One': {
      description: 'Fortune smiles upon your FPL journey, with the fantasy gods regularly blessing your decisions with favorable outcomes.',
      color: 'from-yellow-600 to-yellow-800',
      icon: <Star size={48} className="text-yellow-200" />,
      image: '/images/character-classes/the-favored-one.jpg'
    },
    'The Maverick': {
      description: 'A bold risk-taker who forges their own path, ignoring template teams in favor of differential selections that others overlook.',
      color: 'from-red-600 to-red-800',
      icon: <Zap size={48} className="text-red-200" />,
      image: '/images/character-classes/the-maverick.jpg'
    },
    'The Tactician': {
      description: 'Balancing risk and reward with precision, you make calculated differential selections that give you an edge without excessive gambles.',
      color: 'from-indigo-600 to-indigo-800',
      icon: <Target size={48} className="text-indigo-200" />,
      image: '/images/character-classes/the-tactician.jpg'
    },
    'The Oracle': {
      description: 'Your conservative approach to player selection reveals a deeper wisdom about the game, focusing on reliable performers.',
      color: 'from-blue-600 to-blue-800',
      icon: <Star size={48} className="text-blue-200" />,
      image: '/images/character-classes/the-oracle.jpg'
    },
    'The Guardian': {
      description: 'Steady and reliable, you protect your FPL rank through consistent decision-making without wild swings in performance.',
      color: 'from-teal-700 to-teal-900',
      icon: <Shield size={48} className="text-teal-300" />,
      image: '/images/character-classes/the-guardian.jpg'
    },
    'The Wildborn': {
      description: 'Untamed and unpredictable, your season is marked by dramatic swings in fortune, creating an exciting yet chaotic journey.',
      color: 'from-pink-600 to-pink-900',
      icon: <Activity size={48} className="text-pink-200" />,
      image: '/images/character-classes/the-wildborn.jpg'
    },
    'The Team Zealot': {
      description: 'Your unwavering loyalty to a specific Premier League team influences your FPL selections, for better or worse.',
      color: 'from-purple-600 to-purple-800',
      icon: <Shield size={48} className="text-purple-200" />,
      image: '/images/character-classes/the-team-zealot.jpg'
    },
    'The Chip Virtuoso': {
      description: 'A master of timing chip deployments for maximum effect, consistently extracting exceptional value from these powerful tools.',
      color: 'from-green-600 to-green-900',
      icon: <Zap size={48} className="text-green-200" />,
      image: '/images/character-classes/the-chip-virtuoso.jpg'
    },
    'The Hive Mind': {
      description: 'In tune with the collective wisdom, you favor popular selections and template teams, finding strength in the consensus.',
      color: 'from-amber-700 to-amber-900',
      icon: <Target size={48} className="text-amber-300" />,
      image: '/images/character-classes/the-hive-mind.jpg'
    },
    'The Equilibrium': {
      description: 'Perfectly balanced, as all things should be. Your midtable presence reflects a harmonious balance of strengths and weaknesses.',
      color: 'from-gray-600 to-gray-900',
      icon: <Activity size={48} className="text-gray-300" />,
      image: '/images/character-classes/the-equilibrium.jpg'
    },
    'The Journeyman': {
      description: 'The embodiment of the average FPL experience, your middle-of-the-pack finish represents the classic journey of the fantasy manager.',
      color: 'from-blue-800 to-blue-950',
      icon: <TrendingUp size={48} className="text-blue-400 rotate-45" />,
      image: '/images/character-classes/the-journeyman.jpg'
    },
    'The Tinkerer': {
      description: 'Never content to leave your team alone, your constant adjustments sometimes lead to regret when hastily sold players perform well.',
      color: 'from-violet-700 to-violet-900',
      icon: <Package size={48} className="text-violet-300" />,
      image: '/images/character-classes/the-tinkerer.jpg'
    },
    'The Soothsayer': {
      description: 'Blessed with foresight in captain selections, you rarely miss big hauls from your chosen leaders.',
      color: 'from-cyan-700 to-cyan-900',
      icon: <Trophy size={48} className="text-cyan-300" />,
      image: '/images/character-classes/the-soothsayer.jpg'
    },
    'The Rogue Scout': {
      description: 'With keen eyes for hidden talent, you identify and deploy differential players that others have overlooked.',
      color: 'from-emerald-700 to-emerald-900',
      icon: <Star size={48} className="text-emerald-300" />,
      image: '/images/character-classes/the-rogue-scout.jpg'
    },
    'The Chaos Mage': {
      description: 'Your season is a study in volatility, with wild swings in performance from gameweek to gameweek, confounding prediction.',
      color: 'from-fuchsia-600 to-fuchsia-900',
      icon: <Zap size={48} className="text-fuchsia-300" />,
      image: '/images/character-classes/the-chaos-mage.jpg'
    },
    'The Adventurer': {
      description: 'Still finding your path in the world of FPL, your journey is one of discovery and learning as you develop your unique style.',
      color: 'from-green-700 to-green-900',
      icon: <Target size={48} className="text-green-300" />,
      image: '/images/character-classes/the-adventurer.jpg'
    }
  };
  
  const info = archetypeInfo[archetype] || {
    description: 'A unique FPL manager with a distinctive style.',
    color: 'from-gray-600 to-gray-900',
    icon: <Award size={48} className="text-gray-200" />,
    image: '/images/character-classes/default.jpg'
  };
  
  return (
    <div className="fixed inset-0 flex items-center justify-center z-50" onClick={onClose}>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm"></div>
      <div 
        className={`relative bg-gradient-to-br ${info.color} rounded-xl max-w-4xl w-full shadow-2xl z-10 p-0 m-4 border-2 border-white/20 overflow-hidden transition-all duration-300 ease-in-out ${isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-95'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Add flex container for image and content */}
        <div className="flex flex-col md:flex-row">
          {/* Image section */}
          <div className="w-full md:w-1/2 h-64 md:h-auto relative">
            {!imageLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/50">
                <div className="w-8 h-8 border-4 border-[#3f69ae] border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            <img 
              src={info.image} 
              alt={`${archetype} character class`}
              className="w-full h-full object-cover"
              onLoad={() => setImageLoaded(true)}
              onError={(e) => {
                e.target.onerror = null;
                setImageLoaded(true);
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent md:bg-gradient-to-r"></div>
          </div>
          
          {/* Content section */}
          <div className="w-full md:w-1/2 p-6 relative">
            <div className="flex items-center justify-center mb-4">
              <div className="bg-black/30 p-4 rounded-full">
                {info.icon}
              </div>
            </div>
            <h3 className="text-2xl font-bold text-white text-center mb-4">{archetype}</h3>
            <p className="text-white/90 text-center">{info.description}</p>
            
            <button 
              onClick={onClose}
              className="mt-6 bg-white/10 hover:bg-white/20 text-white px-4 py-2 rounded-full mx-auto block"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// This component has been replaced by the PlayerBadges component

const ManagerProfile = () => {
  const [selectedArchetype, setSelectedArchetype] = useState(null);
  // The selectedBadge state has been moved to the PlayerBadges component
  const { data } = useLeagueData();
  const { selectedManager, getManagerData } = useManagerContext();
  
  // Get all archetypes for the manager
  const getAllArchetypes = () => {
    if (!data || !selectedManager) return [];
    
    try {
      // Check the 'Player Archetypes' sheet from the Excel workbook
      if (data['Player Archetypes']) {
        // Find this manager's row
        const managerData = data['Player Archetypes'].find(row => 
          row.Manager === selectedManager);
        
        if (managerData && managerData.Primary_Archetype) {
          // The Primary_Archetype column contains a list in string format like "['Archetype1', 'Archetype2']"
          // We need to parse it into an actual array
          let archetypes = [];

          if (typeof managerData.Primary_Archetype === 'string') {
            // Parse string representation of list
            try {
              // Remove square brackets and single quotes, then split by comma
              archetypes = managerData.Primary_Archetype
                .replace(/[[\]']/g, '')
                .split(',')
                .map(a => a.trim())
                .filter(a => a.length > 0);  // Remove empty strings
            } catch (e) {
              console.error('Error parsing archetypes string:', e);
            }
          } else if (Array.isArray(managerData.Primary_Archetype)) {
            // It's already an array
            archetypes = managerData.Primary_Archetype;
          }
          
          return archetypes;
        }
      }
    } catch (error) {
      console.error('Error getting archetypes:', error);
    }
    
    // No archetype data available for this manager
    console.log('No archetype data found for manager:', selectedManager);
    return [];
  };
  
  // Get badges for the manager from the Detailed Player Badges sheet if available
  const getManagerBadges = () => {
    if (!data || !selectedManager) return [];
    
    try {
      // First check the 'Detailed Player Badges' sheet from the Excel workbook - this is our preferred source
      if (data['Detailed Player Badges']) {
        console.log('Using Detailed Player Badges sheet for badge data');
        // Get all badges for this manager
        const managerBadges = data['Detailed Player Badges'].filter(row =>
          row.Manager === selectedManager);
        
        if (managerBadges.length > 0) {
          console.log(`Found ${managerBadges.length} badges for ${selectedManager} in Detailed Player Badges`);
          return managerBadges.map(badge => ({
            name: badge.name || badge.Badge_Name || badge.Badge || 'Unknown Badge',
            gameweek: badge.gameweek || badge.Gameweek || badge.GW || 1,
            details: badge.details || badge.Details || badge.description || badge.Description || ''
          }));
        }
      }
      
      // If no detailed badges, check the 'Player Badges' sheet
      if (data['Player Badges']) {
        console.log('Using Player Badges sheet as fallback');
        // Find this manager's row
        const managerData = data['Player Badges'].find(row => 
          row.Manager === selectedManager);
        
        if (managerData && managerData.Badge_Types) {
          // Parse the Badge_Types column which contains a list in string format
          let badgeTypes = [];
          
          if (typeof managerData.Badge_Types === 'string') {
            // Parse string representation of list
            try {
              // Remove square brackets and single quotes, then split by comma
              badgeTypes = managerData.Badge_Types
                .replace(/[[\]']/g, '')
                .split(',')
                .map(b => b.trim())
                .filter(b => b.length > 0);  // Remove empty strings
            } catch (e) {
              console.error('Error parsing badges string:', e);
            }
          } else if (Array.isArray(managerData.Badge_Types)) {
            // It's already an array
            badgeTypes = managerData.Badge_Types;
          }
          
          // If no detailed badges, just return the types with default info
          return badgeTypes.map(badgeType => ({
            name: badgeType,
            gameweek: 1,  // Default gameweek
            details: `Earned the ${badgeType} badge`
          }));
        }
      }
    } catch (error) {
      console.error('Error getting badges:', error);
    }
    
    // No badge data available for this manager
    console.log('No badge data found for manager:', selectedManager);
    return [];
  };
  
  // Get manager's rank, total points, and team value (from latest Rank Timeline entry)
  const getRankInfo = () => {
    if (!data || !selectedManager) return { rank: '?', points: '?', teamValue: '?' };
    try {
      const managers = data.weeklyPoints || [];
      const managerRow = managers.find(row => row.Manager === selectedManager);
      if (managerRow && managerRow.Total) {
        const sorted = [...managers]
          .filter(m => m.Manager && m.Total)
          .sort((a, b) => (b.Total || 0) - (a.Total || 0));
        const rank = sorted.findIndex(m => m.Manager === selectedManager) + 1;

        // Team value from the latest GW in the Rank Timeline
        let teamValue = '?';
        const timeline = (data.rankTimeline || []).filter(r => r.Manager === selectedManager);
        if (timeline.length > 0) {
          const latest = timeline.reduce((a, b) => (b.GW > a.GW ? b : a));
          if (latest.Team_Value != null) teamValue = latest.Team_Value;
        }

        return { rank: rank > 0 ? rank : '?', points: managerRow.Total, teamValue };
      }
    } catch (e) {
      console.error('Error getting rank info:', e);
    }
    return { rank: '?', points: '?', teamValue: '?' };
  };
  
  // Calculate risk profile metrics
  const calculateRiskProfile = () => {
    if (!data || !selectedManager) return {
      riskScore: 50,
      riskCategory: 'Balanced Manager',
      differentialPick: { name: 'Unknown', ownership: 0, points: 0, gameweek: 0 },
      differentialPercentage: 15.3,
      riskPercentile: 50
    };

    let riskScore = 50;
    let riskCategory = 'Balanced Manager';
    let differentialPick = { name: 'Unknown', ownership: 0, points: 0, gameweek: 0 };
    let differentialPercentage = 15.3;
    let riskPercentile = 50;

    try {
      // First check for data in the 'Risk Profile' tab
      if (data['Risk Profile']) {
        console.log('Risk Profile tab found, searching for manager:', selectedManager);
        
        // Find this manager's row
        const managerRisk = data['Risk Profile'].find(row => row.Manager === selectedManager);
        
        if (managerRisk) {
          console.log('Found manager in Risk Profile tab:', managerRisk);
          
          // Get risk score - prioritize Risk_Score, then Risk_Profile, then fall back to calculations
          if (managerRisk.Risk_Score !== undefined) {
            riskScore = managerRisk.Risk_Score;
          } else if (managerRisk.Risk_Profile !== undefined) {
            // If it's a text value, convert to a numeric score
            const riskValue = managerRisk.Risk_Profile;
            if (typeof riskValue === 'string') {
              if (riskValue.includes('Ultra-Differential') || riskValue.includes('Maverick')) {
                riskScore = 90;
              } else if (riskValue.includes('High Risk') || riskValue.includes('Risk Taker')) {
                riskScore = 75;
              } else if (riskValue.includes('Balanced')) {
                riskScore = 50;
              } else if (riskValue.includes('Conservative')) {
                riskScore = 30;
              } else if (riskValue.includes('Template')) {
                riskScore = 10;
              }
            } else if (typeof riskValue === 'number') {
              riskScore = riskValue;
            }
          } else if (managerRisk.Avg_Differential_Percentage !== undefined) {
            // Calculate from differential percentage
            differentialPercentage = managerRisk.Avg_Differential_Percentage;
            riskScore = Math.min(differentialPercentage * 2, 100); // Scale to 0-100
          }
          
          // Get risk category
          if (managerRisk.Risk_Category) {
            riskCategory = managerRisk.Risk_Category;
          } else if (managerRisk.Risk_Profile) {
            riskCategory = managerRisk.Risk_Profile;
          } else {
            // Determine category based on score
            if (riskScore >= 80) {
              riskCategory = "Ultra-Differential Maverick";
            } else if (riskScore >= 65) {
              riskCategory = "High Risk Taker";
            } else if (riskScore >= 45) {
              riskCategory = "Balanced Manager";
            } else if (riskScore >= 30) {
              riskCategory = "Conservative";
            } else {
              riskCategory = "Template Follower";
            }
          }
          
          // Get percentile if available
          if (managerRisk.Risk_Percentile !== undefined) {
            riskPercentile = managerRisk.Risk_Percentile;
          } else if (managerRisk.Percentile !== undefined) {
            riskPercentile = managerRisk.Percentile;
          }
          
          // Get differential percentage
          if (managerRisk.Avg_Differential_Percentage !== undefined) {
            differentialPercentage = managerRisk.Avg_Differential_Percentage;
          } else if (managerRisk.Differential_Percentage !== undefined) {
            differentialPercentage = managerRisk.Differential_Percentage;
          }
          
          // Get best differential pick
          if (managerRisk.Best_Differential_Player) {
            differentialPick = {
              name: managerRisk.Best_Differential_Player,
              ownership: managerRisk.Best_Differential_Ownership || 5,
              points: managerRisk.Best_Differential_Points || 10,
              gameweek: managerRisk.Best_Differential_GW || 1
            };
          } else if (managerRisk.Best_Differential) {
            // Try to parse best differential from a string format like "Palmer (4.8% owned) in GW5, 15 points"
            const diffString = managerRisk.Best_Differential;
            if (typeof diffString === 'string') {
              const nameMatch = diffString.match(/([A-Za-z\s-]+)\s*\(/); // Match name before first parenthesis
              const ownershipMatch = diffString.match(/\((\d+\.?\d*)%/); // Match ownership percentage
              const gwMatch = diffString.match(/GW(\d+)/i); // Match gameweek number
              const pointsMatch = diffString.match(/(\d+)\s*points/i); // Match points
              
              differentialPick = {
                name: nameMatch ? nameMatch[1].trim() : 'Unknown',
                ownership: ownershipMatch ? parseFloat(ownershipMatch[1]) : 5,
                points: pointsMatch ? parseInt(pointsMatch[1], 10) : 10,
                gameweek: gwMatch ? parseInt(gwMatch[1], 10) : 1
              };
            }
          }
        }
      }
      
      // If we didn't find risk data, check other sheets
      // First try 'Differential Players Summary' which has some risk-related metrics
      if ((!data['Risk Profile'] || riskScore === 50) && data['Differential Players Summary']) {
        const diffData = data['Differential Players Summary'];
        const managerDiff = diffData.find(row => row.Manager === selectedManager);
        
        if (managerDiff) {
          // Use differential percentage to estimate risk score
          if (managerDiff.Differential_Percentage !== undefined) {
            differentialPercentage = managerDiff.Differential_Percentage;
            // Transform to 0-100 scale where more differential % = higher risk
            riskScore = Math.min(differentialPercentage * 2, 100);
          }
          
          // Get best differential pick
          if (managerDiff.Best_Differential_Player) {
            differentialPick = {
              name: managerDiff.Best_Differential_Player,
              ownership: managerDiff.Best_Differential_Ownership || 5,
              points: managerDiff.Best_Differential_Points || 10,
              gameweek: managerDiff.Best_Differential_GW || 1
            };
          }
        }
      }
      
      // Also check 'Player Analysis' if available as a third fallback
      if (riskScore === 50 && data['Player Analysis']) {
        const playerData = data['Player Analysis'];
        const managerData = playerData.find(row => row.Manager === selectedManager);
        
        if (managerData) {
          if (managerData.Differential_Score !== undefined) {
            riskScore = managerData.Differential_Score;
          }
          
          if (managerData.Differential_Category) {
            riskCategory = managerData.Differential_Category;
          }
          
          if (managerData.Differential_Percentage !== undefined) {
            differentialPercentage = managerData.Differential_Percentage;
          }
        }
      }
      
      console.log('Calculated risk profile for', selectedManager, ':', {
        riskScore,
        riskCategory,
        differentialPercentage,
        riskPercentile
      });
    } catch (error) {
      console.error('Error calculating risk profile:', error);
    }
    
    return {
      riskScore,
      riskCategory,
      differentialPick,
      differentialPercentage,
      riskPercentile
    };
  };

  // Calculate luck and consistency scores
  const calculateMetrics = () => {
    if (!data || !selectedManager) return { luckScore: 50, consistencyScore: 50, consistencyCategory: 'Moderately Consistent', consistencyDescription: 'Some ups and downs, but generally maintaining a steady course through the season.' };
    
    let luckScore = 50;
    let consistencyScore = 50;
    let consistencyCategory = 'Moderately Consistent';
    let consistencyDescription = 'Some ups and downs, but generally maintaining a steady course through the season.';
    let luckCategory = 'Average Luck';
    
    try {
      // Get luck score from 'Luck Meter' sheet as specified
      if (data['Luck Meter']) {
        const luckData = data['Luck Meter'];
        
        // Find this manager's row
        const managerLuck = luckData.find(row => row.Manager === selectedManager);
        
        if (managerLuck && managerLuck.Overall_Luck_Score !== undefined) {
          // The higher the Overall_Luck_Score, the more unlucky the manager
          // We need to invert and rescale it for the frontend to show blessed as full
          // First, we get min and max luck scores across all managers to normalize
          let minLuckScore = 100;
          let maxLuckScore = 0;
          
          luckData.forEach(manager => {
            if (manager.Overall_Luck_Score !== undefined) {
              minLuckScore = Math.min(minLuckScore, manager.Overall_Luck_Score);
              maxLuckScore = Math.max(maxLuckScore, manager.Overall_Luck_Score);
            }
          });
          
          // Store the raw score for category determination
          const rawLuckScore = managerLuck.Overall_Luck_Score;
          
          // Calculate the range and normalize
          const luckRange = maxLuckScore - minLuckScore;
          if (luckRange > 0) {
            // Normalize to 0-100 scale and invert (subtract from 100)
            // This makes the luckiest player (lowest raw score) have the highest display score
            // Apply a more moderate scaling factor to spread things out moderately
            const scalingFactor = 1.2;
            luckScore = 100 - (((rawLuckScore - minLuckScore) / luckRange) * 100);
            
            // Apply scaling factor but in a more moderate way
            if (luckScore > 50) {
              // Stretch scores above 50 (lucky side) but less aggressively
              luckScore = 50 + ((luckScore - 50) * scalingFactor);
            } else if (luckScore < 50) {
              // Stretch scores below 50 (unlucky side) but less aggressively
              luckScore = 50 - ((50 - luckScore) * scalingFactor);
            }
            // No change to exactly 50
            
            // Cap at 0-100
            luckScore = Math.min(Math.max(luckScore, 0), 100);
          }
          
          // Get luck category if available
          if (managerLuck.Luck_Category) {
            luckCategory = managerLuck.Luck_Category;
          } else {
            // Determine category based on original raw score
            if (rawLuckScore >= 75) {
              luckCategory = "Extremely Unlucky";
            } else if (rawLuckScore >= 60) {
              luckCategory = "Unlucky";
            } else if (rawLuckScore >= 40) {
              luckCategory = "Average Luck";
            } else if (rawLuckScore >= 25) {
              luckCategory = "Lucky";
            } else {
              luckCategory = "Extremely Lucky";
            }
          }
        }
      }
      
      // Get consistency score from 'Consistency Analysis' sheet
      if (data['Consistency Analysis']) {
        const consistencyData = data['Consistency Analysis'];
        
        // Find this manager's row
        const managerConsistency = consistencyData.find(row => row.Manager === selectedManager);
        
        if (managerConsistency) {
          // Use Consistency_Score if available
          if (managerConsistency.Consistency_Score !== undefined) {
            consistencyScore = managerConsistency.Consistency_Score;
          }
          
          // If Consistency_Rank is available, use it for a more accurate representation
          if (managerConsistency.Consistency_Rank !== undefined) {
            // Get the total number of managers to normalize the rank
            const totalManagers = consistencyData.length;
            
            if (totalManagers > 0) {
              // Convert rank (where 1 is best) to a 0-100 scale (where 100 is best)
              // For example, if they're ranked 1st out of 10 managers, they get 100
              // If they're ranked 10th out of 10, they get 0
              consistencyScore = 100 * (1 - ((managerConsistency.Consistency_Rank - 1) / (totalManagers - 1)));
              
              // Apply a moderate scaling to spread things out
              const scalingFactor = 1.2;
              if (consistencyScore > 50) {
                consistencyScore = 50 + ((consistencyScore - 50) * scalingFactor);
              } else if (consistencyScore < 50) {
                consistencyScore = 50 - ((50 - consistencyScore) * scalingFactor);
              }
              
              // Cap at 0-100
              consistencyScore = Math.min(Math.max(consistencyScore, 0), 100);
            }
          }
          
          // Set appropriate consistency category and description based on the score
          // These need to align with the new rank-based score
          if (consistencyScore >= 80) {
            consistencyCategory = "Rock Solid";
            consistencyDescription = "Remarkably consistent performance throughout the season. The epitome of reliability.";
          } else if (consistencyScore >= 65) {
            consistencyCategory = "Steady Performer";
            consistencyDescription = "Maintains a steady course with minimal fluctuations. A dependable performer.";
          } else if (consistencyScore >= 50) {
            consistencyCategory = "Moderately Consistent";
            consistencyDescription = "Some ups and downs, but generally maintaining a steady course through the season.";
          } else if (consistencyScore >= 35) {
            consistencyCategory = "Unpredictable";
            consistencyDescription = "Performance varies noticeably from week to week, making it hard to predict results.";
          } else if (consistencyScore >= 20) {
            consistencyCategory = "Volatile";
            consistencyDescription = "Significant swings in performance create a rollercoaster season of highs and lows.";
          } else {
            consistencyCategory = "Extreme Rollercoaster";
            consistencyDescription = "Your season has been a wild ride of extreme highs and lows. A true rollercoaster journey!";
          }
        }
      } else {
        // Fallback to calculating consistency if the sheet isn't available
        console.log('Consistency Analysis sheet not found, calculating from weekly points');
        
        // Calculate consistency based on weekly points rank variability
        if (data.weeklyPoints) {
          const weeklyPoints = data.weeklyPoints;
          const gameweeks = weeklyPoints
            .filter(wp => wp.Manager === selectedManager)
            .map(wp => wp.GW)
            .sort((a, b) => a - b);
          
          if (gameweeks.length > 0) {
            // For each gameweek, find the manager's rank
            const ranks = [];
            
            gameweeks.forEach(gw => {
              // Get all managers' scores for this gameweek
              const gwScores = weeklyPoints
                .filter(wp => wp.GW === gw)
                .map(wp => ({
                  manager: wp.Manager,
                  points: wp.Points
                }));
              
              // Sort by points to determine ranks
              const sortedScores = [...gwScores].sort((a, b) => b.points - a.points);
              
              // Find this manager's rank
              const rank = sortedScores.findIndex(s => s.manager === selectedManager) + 1;
              if (rank > 0) ranks.push(rank);
            });
            
            if (ranks.length > 0) {
              // Calculate standard deviation of ranks
              const avgRank = ranks.reduce((sum, r) => sum + r, 0) / ranks.length;
              const squareDiffs = ranks.map(r => Math.pow(r - avgRank, 2));
              const avgSquareDiff = squareDiffs.reduce((sum, d) => sum + d, 0) / squareDiffs.length;
              const stdDev = Math.sqrt(avgSquareDiff);
              
              // Max possible std dev would be if ranks alternated between 1 and last place
              const managerCount = weeklyPoints
                .filter(wp => wp.GW === gameweeks[0])
                .length;
              
              const maxPossibleStdDev = (managerCount - 1) / 2;
              
              // Convert to 0-100 scale where 100 is most consistent (lowest std dev)
              // The more consistent, the lower the std dev of ranks
              consistencyScore = Math.min(Math.max(100 - ((stdDev / maxPossibleStdDev) * 100), 0), 100);
              
              // Set category based on score
              if (consistencyScore >= 80) {
                consistencyCategory = "Rock Solid";
                consistencyDescription = "Remarkably consistent performance throughout the season. The epitome of reliability.";
              } else if (consistencyScore >= 65) {
                consistencyCategory = "Steady Performer";
                consistencyDescription = "Maintains a steady course with minimal fluctuations. A dependable performer.";
              } else if (consistencyScore >= 50) {
                consistencyCategory = "Moderately Consistent";
                consistencyDescription = "Some ups and downs, but generally maintaining a steady course through the season.";
              } else if (consistencyScore >= 35) {
                consistencyCategory = "Unpredictable";
                consistencyDescription = "Performance varies noticeably from week to week, making it hard to predict results.";
              } else if (consistencyScore >= 20) {
                consistencyCategory = "Volatile";
                consistencyDescription = "Significant swings in performance create a rollercoaster season of highs and lows.";
              } else {
                consistencyCategory = "Extreme Rollercoaster";
                consistencyDescription = "Your season has been a wild ride of extreme highs and lows. A true rollercoaster journey!";
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error calculating metrics:', error);
    }
    
    return { luckScore, consistencyScore, consistencyCategory, consistencyDescription, luckCategory };
  };
  
  // Get archetypes and badges
  const archetypes = getAllArchetypes();
  const badges = getManagerBadges();
  
  // Get luck and consistency scores
  const { luckScore, consistencyScore, consistencyCategory, consistencyDescription, luckCategory } = calculateMetrics();
  
  // Get risk profile data
  const riskProfile = calculateRiskProfile();
  
  // Get managerData and rank info
  const managerData = getManagerData();
  const rankInfo = getRankInfo();
  
  // Render football formation visualization
  const renderFormation = (formation) => {
    // Default to 4-4-2 if no formation provided
    const formationString = formation || '4-4-2';
    
    // Parse formation string to get number of defenders, midfielders, and forwards
    const [def, mid, fwd] = formationString.split('-').map(num => parseInt(num, 10));
    
    return (
      <div className="h-full w-full bg-green-900/60 rounded-lg overflow-hidden flex flex-col items-center justify-between py-4 relative">
        {/* Field markings */}
        <div className="absolute inset-0 flex flex-col items-center">
          {/* Center circle */}
          <div className="w-16 h-16 border-2 border-white/20 rounded-full mt-20"></div>
          {/* Center line */}
          <div className="w-full h-px bg-white/20 absolute top-1/2"></div>
          {/* Penalty areas */}
          <div className="w-3/4 h-12 border-2 border-white/20 absolute top-1 rounded-b-lg"></div>
          <div className="w-3/4 h-12 border-2 border-white/20 absolute bottom-1 rounded-t-lg"></div>
        </div>
        
        {/* Goalkeeper */}
        <div className="z-10 mb-1">
          <div className="w-4 h-4 bg-yellow-400 rounded-full mx-auto"></div>
        </div>
        
        {/* Defenders */}
        <div className="z-10 flex justify-around w-full px-4 mb-1">
          {Array.from({ length: def }).map((_, i) => (
            <div key={`def-${i}`} className="w-4 h-4 bg-blue-400 rounded-full"></div>
          ))}
        </div>
        
        {/* Midfielders */}
        <div className="z-10 flex justify-around w-full px-4 mb-1">
          {Array.from({ length: mid }).map((_, i) => (
            <div key={`mid-${i}`} className="w-4 h-4 bg-green-400 rounded-full"></div>
          ))}
        </div>
        
        {/* Forwards */}
        <div className="z-10 flex justify-around w-full px-4">
          {Array.from({ length: fwd }).map((_, i) => (
            <div key={`fwd-${i}`} className="w-4 h-4 bg-red-400 rounded-full"></div>
          ))}
        </div>
      </div>
    );
  };

  // Debug data availability
  useEffect(() => {
    if (data && selectedManager) {
      // Log available data
      console.log('Available data keys:', Object.keys(data));
      
      // Debug specific sheets
      if (data['Weekly Points']) {
        console.log('Weekly Points sheet available, example row:', data['Weekly Points'][0]);
      }
      
      if (data['Player Archetypes']) {
        console.log('Player Archetypes sheet available, manager rows:', 
          data['Player Archetypes'].filter(row => row.Manager === selectedManager));
      }
      
      if (data['Player Badges']) {
        console.log('Player Badges sheet available, manager rows:', 
          data['Player Badges'].filter(row => row.Manager === selectedManager));
      }
      
      if (data['Luck Meter']) {
        console.log('Luck Meter sheet available, manager rows:', 
          data['Luck Meter'].filter(row => row.Manager === selectedManager));
      }
      
      // Log what was found
      console.log('Selected manager:', selectedManager);
      console.log('Archetypes found:', archetypes);
      console.log('Badges found:', badges);
      console.log('Rank info:', rankInfo);
      console.log('Metrics:', { 
        luckScore, 
        consistencyScore,
        luckCategory,
        consistencyCategory,
        consistencyRank: data['Consistency Analysis']?.find(row => row.Manager === selectedManager)?.Consistency_Rank || 'N/A'
      });
    }
  }, [data, selectedManager, archetypes, badges, rankInfo, luckScore, consistencyScore, luckCategory, consistencyCategory]);

  // If no manager is selected, show a message
  if (!selectedManager) {
    return (
      <div className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center">
        <h3 className="text-xl font-bold mb-4">No manager selected</h3>
        <p className="text-gray-300">Please select a manager to view their profile.</p>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      {/* Character Sheet Header */}
      <div className="rounded-xl overflow-hidden shadow-xl" style={{ background: '#0d1524', border: '2px solid rgba(63,105,174,0.5)' }}>
        <div className="bg-black/30 backdrop-blur-sm p-5" style={{ borderBottom: '2px solid rgba(63,105,174,0.3)' }}>
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center w-full">
              <h3 className="text-2xl md:text-3xl font-bold text-white tracking-wide">
                {selectedManager}
              </h3>
              <div className="flex items-center space-x-4 mt-2 md:mt-0">
                <div className="bg-[#0a1628] rounded-lg px-4 py-2 text-center border border-[#3f69ae]/30 shadow-lg">
                  <div className="text-[#44d5a3] text-sm font-medium">Rank</div>
                  <div className="text-2xl font-bold text-white">{rankInfo.rank}</div>
                </div>
                <div className="bg-[#0a1628] rounded-lg px-4 py-2 text-center border border-[#3f69ae]/30 shadow-lg">
                  <div className="text-[#44d5a3] text-sm font-medium">Points</div>
                  <div className="text-2xl font-bold text-white">{rankInfo.points}</div>
                </div>
                <div className="bg-[#0a1628] rounded-lg px-4 py-2 text-center border border-[#3f69ae]/30 shadow-lg">
                  <div className="text-[#44d5a3] text-sm font-medium">Team Value</div>
                  <div className="text-2xl font-bold text-white">{typeof rankInfo.teamValue === 'number' ? `£${rankInfo.teamValue.toFixed(1)}m` : rankInfo.teamValue}</div>
                </div>
              </div>
            </div>
        </div>
        
        {/* Favorite Formation Section */}
        {managerData?.formations && (
          <div className="p-5 bg-black/20 border-b-2 border-[#3f69ae]/30">
            <h4 className="text-lg font-medium text-white mb-4 flex items-center">
              <Dribbble className="mr-2 text-[#44d5a3]" size={20} />
              Tactical Approach
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {/* Formation Visual */}
              <div className="bg-black/30 p-4 rounded-lg border border-[#3f69ae]/20 flex flex-col items-center">
                <div className="relative w-full h-40 max-w-[180px] mx-auto">
                  {renderFormation(managerData.formations.mostCommonFormation)}
                </div>
                <div className="mt-2 text-center">
                  <div className="text-xl font-bold text-white">{managerData.formations.mostCommonFormation}</div>
                  <div className="text-[#44d5a3] text-sm">Most Used Formation</div>
                </div>
              </div>
              
              {/* Usage Stats */}
              <div className="bg-black/30 p-4 rounded-lg border border-[#3f69ae]/20">
                <div className="text-center mb-2">
                  <span className="text-2xl font-bold text-white">{managerData.formations.formationUsagePercent.toFixed(1)}%</span>
                </div>
                <div className="h-2 bg-gray-700 rounded-full mb-2">
                  <div 
                    className="h-full bg-[#3f69ae] rounded-full" 
                    style={{ width: `${Math.min(managerData.formations.formationUsagePercent, 100)}%` }}
                  ></div>
                </div>
                <div className="text-[#44d5a3] text-sm text-center">Formation Consistency</div>
                <div className="text-white/70 text-xs text-center mt-1">
                  {managerData.formations.formationUsagePercent >= 75 ? 
                    "You're extremely loyal to your preferred formation!" :
                    managerData.formations.formationUsagePercent >= 50 ?
                      "You rely heavily on this formation, but aren't afraid to adapt." :
                      "You're quite flexible with your formations throughout the season."  
                  }
                </div>
              </div>
              
              {/* Variety Stats */}
              <div className="bg-black/30 p-4 rounded-lg border border-[#3f69ae]/20">
                <div className="text-center">
                  <span className="text-2xl font-bold text-white">{managerData.formations.formationVariety || 0}</span>
                  <span className="text-white ml-2">Different Formations</span>
                </div>
                
                <div className="mt-4 text-center">
                  <div className="inline-block px-3 py-1 rounded-full bg-[#3f69ae]/20 text-[#44d5a3] text-sm font-medium">
                    {managerData.formations.formationVariety >= 5 ? "Tactically Flexible" :
                     managerData.formations.formationVariety >= 3 ? "Moderately Adaptable" :
                     "Tactically Consistent"}
                  </div>
                </div>
                
                <div className="text-white/70 text-xs text-center mt-3">
                  {managerData.formations.matchesLeagueFormation ? 
                    "Your preferred formation matches the league's most popular choice." :
                    "You prefer a different formation than the majority of the league."
                  }
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Manager Archetypes — cinematic reveal (Feature 3) */}
        <div className="p-5 bg-black/20">
          <ArchetypeReveal />
        </div>
        
        {/* Luck, Consistency, & Risk Meters */}
        <div className="p-5 bg-black/30">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Luck Meter */}
            <div className="bg-black/30 rounded-lg p-4 border border-[#3f69ae]/20">
              <h4 className="text-lg font-medium text-[#44d5a3] mb-2 flex items-center">
                <Zap size={18} className="mr-2 text-amber-400" />
                Luck Meter <span className="inline-block ml-1">{luckScore >= 75 ? "🍀" : luckScore <= 25 ? "☠️" : ""}</span>
              </h4>
              <div className="h-4 bg-gray-700 rounded-full overflow-hidden mb-2">
                <div 
                  className="h-full bg-gradient-to-r from-red-500 via-amber-500 to-green-500 relative"
                  style={{ width: `${luckScore}%` }}
                >
                  {/* Small pointer indicator at the exact score */}
                  <div className="absolute right-0 top-0 h-full w-1 bg-white"></div>
                </div>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>Cursed</span>
                <span>Neutral</span>
                <span>Blessed</span>
              </div>
              <p className="mt-3 text-sm text-gray-300">
                {luckScore >= 66 ? 
                  "The fantasy gods have been kind to you this season! Your transfer and captain choices were rewarded beyond expectation." :
                  luckScore <= 33 ? 
                    "Fortune has been cruel - your decisions deserved better returns. Bench points, captaincy disappointments, and transfer regrets have piled up." :
                    "A balanced season of fortune. Some luck, some skill - the FPL gods have been fair to you."
                }
              </p>
              <p className="mt-1 text-xs text-amber-400 font-medium">
                Score: {Math.round(luckScore)}/100 • {luckCategory}
              </p>
            </div>
            
            {/* Consistency Meter */}
            <div className="bg-black/30 rounded-lg p-4 border border-[#3f69ae]/20">
              <h4 className="text-lg font-medium text-[#44d5a3] mb-2 flex items-center">
                <Activity size={18} className="mr-2 text-blue-400" />
                Consistency <span className="inline-block ml-1">{consistencyScore >= 75 ? "🏆" : consistencyScore <= 25 ? "🎢" : ""}</span>
              </h4>
              <div className="h-4 bg-gray-700 rounded-full overflow-hidden mb-2">
                <div 
                  className="h-full bg-gradient-to-r from-[#3f69ae] via-[#44b6c5] to-[#44d5a3] relative"
                  style={{ width: `${consistencyScore}%` }}
                >
                  {/* Small pointer indicator at the exact score */}
                  <div className="absolute right-0 top-0 h-full w-1 bg-white"></div>
                </div>
              </div>
              <div className="flex justify-between text-xs text-gray-400">
                <span>Rollercoaster</span>
                <span>Varying</span>
                <span>Steady</span>
              </div>
              <p className="mt-3 text-sm text-gray-300">
                {consistencyDescription}
              </p>
              <p className="mt-1 text-xs text-[#44b6c5] font-medium">
                Score: {Math.round(consistencyScore)}/100 • {consistencyCategory}
              </p>
            </div>
            
            {/* Risk Profile Meter - New Component */}
            <RiskProfileSection riskProfile={riskProfile} />
          </div>
        </div>
      </div>
      
      {/* Player Badges */}
      <PlayerBadges badges={badges} />
      
      {/* Modal for archetype details */}
      {selectedArchetype && (
        <ArchetypeDetails 
          archetype={selectedArchetype} 
          onClose={() => setSelectedArchetype(null)} 
        />
      )}
      
      {/* Badge details modal has been moved to the PlayerBadges component */}
      
      {/* Add these styles to your CSS */}
      <style jsx>{`
        @keyframes shine {
          0% { transform: translateX(-100%) rotate(-45deg); }
          100% { transform: translateX(100%) rotate(-45deg); }
        }
        
        .animate-shine {
          animation: shine 2s infinite linear;
        }
        
        @keyframes ping-slow {
          0% { transform: scale(1); opacity: 0.8; }
          50% { transform: scale(1.5); opacity: 0; }
          100% { transform: scale(1); opacity: 0; }
        }
        
        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }
        
        .shine-animation {
          transition: opacity 0.3s ease;
        }
      `}</style>
    </div>
  );
};

export default ManagerProfile;
